import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react'
import {
  addTicketComment,
  markTicketCommentsRead,
  ticketCommentFormQuery,
  ticketCommentsQuery,
  type TicketComment,
  type TicketCommentFormPage,
  type TicketCommentReply,
} from '../api/easeedesk'
import { fromNow, stamp } from '../inventoree/format'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck, SettingsRadio } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { TicketCreateForm } from './TicketCreateForm'
import './TicketCommentForm.css'

/**
 * Comentariile unui tichet - fereastra "New comment" din lista si fila
 * "Collaborate" a fisei.
 *
 * DOUA INFATISARI, ACELASI CAP DE SCRIERE (`POST /easeedesk/tickets/{id}/comment`).
 *
 * In fereastra din lista (`showCommentModal`) formularul e cel din
 * `easeedesk/forms/commentForm.php`: eticheta, casuta, bifele `inform-client`
 * / `request_files` una sub alta, butoanele la dreapta dupa o linie.
 *
 * In fila "Collaborate" (`panel`) e ce desena `PComments` din
 * `public/js/easeedesk/easeedesk.js` peste `ticket/comments/index.php`:
 *
 *   - SUS, `comments-history-<id>`: lista comentariilor
 *     (`easeedesk/commentsLists/<id>`), cele mai noi primele, fiecare cu
 *     pastila felului, autorul, vizibilitatea (public / private / internal),
 *     de cand, textul, butonul "Reply" care desface un formular de raspuns sub
 *     comentariu, si raspunsurile firului dedesubt, trase la dreapta;
 *   - JOS, `new-comment-<id>`: cutia cu umbra - "Refresh list", lista de
 *     SABLOANE (`collaborateTemplateLists`), casuta inalta, cele trei butoane
 *     radio de vizibilitate (Internal / Ticket Info / Sent to Agent), felurile
 *     proprii de comentariu ale furnizorului, fisierele, "Add comment",
 *     "Attach file" si "Hide".
 *
 * CINE MARCHEAZA CITITE. In portal, chiar deschiderea filei
 * (`Easeedesk::ticketComments`, un GET) marca comentariile citite si cobora
 * cifra din clopotelul de colaborare. Aici lista se CITESTE, iar marcarea e
 * un POST pe care fila il trimite dupa ce a aratat lista - acelasi efect, dar
 * fara o citire care scrie.
 *
 * PE UN TICHET REZOLVAT, un comentariu nou REDESCHIDE tichetul (asa face si
 * portalul, prin `ReopenTicket::reopenFromCollaborate`): fila spune asta
 * inainte, iar dupa trimitere fisa se reciteste intreaga, fiindca s-a
 * schimbat statusul, nu doar lista. Pe un tichet terminat nu e casuta -
 * "Comments are closed" - si nici butonul "Reply" pe comentarii.
 *
 * FISIERELE pleaca odata cu comentariul, in `files[]`, ca in portal; serverul
 * le urca pe serverul de descarcari si le lipeste la email.
 */

/** Cate pastile de vizibilitate: `PComments.getCommentVisibility()`. */
const VISIBILITY_BADGE: Record<number, { className: string; label: string }> = {
  1: { className: 'bg-success', label: 'public' },
  2: { className: 'bg-warning', label: 'private' },
  3: { className: 'bg-danger', label: 'internal' },
}

function VisibilityBadge({ visibility }: { visibility: number }) {
  const badge = VISIBILITY_BADGE[visibility]
  if (!badge) return null
  return (
    <>
      (<span className={`badge mini-badge ${badge.className}`}>{badge.label}</span>)
    </>
  )
}

/** `moment(created).fromNow()`, cum il scria PComments dupa liniuta. */
function since(created: string, now: Date): string {
  const when = stamp(created)
  return when === null ? created : fromNow(when, now)
}

/* ------------------------------------------------------------------------ */
/*  Fisierele de atasat: `addFile` / `previewFile` / `removeFile`            */
/* ------------------------------------------------------------------------ */

function AttachedFiles({ files, onRemove }: { files: File[]; onRemove(index: number): void }) {
  return (
    <div className="comment-attachments-area tcf-files">
      {files.map((file, index) => (
        <div className="preview-file" key={`${file.name}-${index}`}>
          <span className="preview-file-name" title={file.name}>
            {file.name} ({file.size} bytes)
          </span>
          <button
            type="button"
            className="btn btn-danger btn-sm pull-right remove-comment-file"
            title="Remove"
            aria-label={`Remove ${file.name}`}
            onClick={() => onRemove(index)}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>
      ))}
    </div>
  )
}

function useFiles() {
  const [files, setFiles] = useState<File[]>([])
  const input = useRef<HTMLInputElement>(null)
  const pick = () => input.current?.click()
  const onChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const chosen = ev.target.files ? Array.from(ev.target.files) : []
    if (chosen.length > 0) setFiles((have) => [...have, ...chosen])
    ev.target.value = ''
  }
  const remove = (index: number) => setFiles((have) => have.filter((_, i) => i !== index))
  const reset = () => setFiles([])
  return { files, input, pick, onChange, remove, reset }
}

/* ------------------------------------------------------------------------ */
/*  Fereastra din lista: `forms/commentForm.php`                             */
/* ------------------------------------------------------------------------ */

export function TicketCommentForm({ id, onDone, panel = false }: { id: number; onDone?: () => void; panel?: boolean }) {
  if (panel) return <TicketCollaborate id={id} />
  return <TicketCommentModalForm id={id} onDone={onDone} />
}

function TicketCommentModalForm({ id, onDone }: { id: number; onDone?: () => void }) {
  const { data, error } = useQuery(ticketCommentFormQuery(id))
  const client = useQueryClient()
  const [text, setText] = useState('')
  const [inform, setInform] = useState(false)
  const [wantFiles, setWantFiles] = useState(false)
  const attach = useFiles()

  const send = useMutation({
    mutationFn: () => addTicketComment(id, { comment: text, informClient: inform, requestFiles: wantFiles, files: attach.files }),
    onSuccess: () => {
      setText('')
      setInform(false)
      setWantFiles(false)
      attach.reset()
      /* Insignele filelor si randul de sus se schimba odata cu comentariul. */
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
      onDone?.()
    },
  })

  if (error) return <Failed message={error.message} />
  if (!data) return <Spinner />

  /* Starea finala: nici casuta, nici butoanele - numai cuvintele portalului. */
  if (!data.canComment) return <Notice notice={data.notice} />

  const boxes: Record<string, { checked: boolean; set(next: boolean): void }> = {
    'inform-client': { checked: inform, set: setInform },
    request_files: { checked: wantFiles, set: setWantFiles },
  }

  return (
    <>
      {data.mode === 'reopen' && <Notice notice={data.notice} />}
      <form
        method="post"
        action={data.action}
        className="addCommentFormModal"
        onSubmit={(ev) => {
          ev.preventDefault()
          if (text.trim() === '' || send.isPending) return
          send.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor={`message-text-${id}`} className="control-label">
            {data.label} {data.required && <span className="required">*</span>}
          </label>
          <textarea
            className="form-control"
            name="comment"
            id={`message-text-${id}`}
            required={data.required}
            value={text}
            onChange={(ev) => setText(ev.target.value)}
          ></textarea>
          <input type="hidden" name="ticket" value={data.ticket} />
        </div>

        {data.checkboxes.map((box) => (
          <div className="checkbox" key={box.name}>
            <SettingsCheck
              name={box.name}
              checked={boxes[box.name]?.checked ?? false}
              onChange={(on) => boxes[box.name]?.set(on)}
              label={box.label}
            />
          </div>
        ))}

        <AttachedFiles files={attach.files} onRemove={attach.remove} />
        {send.isError && <p className="text-danger">{(send.error as Error).message}</p>}
        <div className="form-group align-right">
          <hr />
          <input type="file" name="files[]" className="hidden comment-attachments" ref={attach.input} onChange={attach.onChange} />
          <button type="submit" className="btn btn-sm btn-primary submit-comment" disabled={text.trim() === '' || send.isPending}>
            {send.isPending ? 'Saving...' : data.submit}
          </button>{' '}
          <button type="button" className="btn btn-default btn-sm attach-file-comment" onClick={attach.pick}>
            <i className="fa fa-paperclip" aria-hidden="true"></i> {data.attach}
          </button>
        </div>
      </form>
    </>
  )
}

function Failed({ message }: { message: string }) {
  return (
    <div className="alert alert-danger" role="alert">
      Something went wrong: {message}
    </div>
  )
}

/* `closedCommentsAlert()` si `reopenAlert()`: aceeasi cutie, alte vorbe. */
function Notice({ notice }: { notice: string }) {
  return (
    <div className="alert alert-info" role="alert">
      <i className="fas fa-info-circle" aria-hidden="true"></i> {notice}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Fila "Collaborate": `ticket/comments/index.php` + `PComments`            */
/* ------------------------------------------------------------------------ */

export function TicketCollaborate({ id }: { id: number }) {
  const form = useQuery(ticketCommentFormQuery(id))
  const list = useQuery(ticketCommentsQuery(id))
  const client = useQueryClient()
  const [hidden, setHidden] = useState(false)
  const [creating, setCreating] = useState(false)

  /*
   * Marcarea ca citite: o data la deschidere si de fiecare data cand lista
   * s-a recitit cu ceva necitit in ea. Clopotelul de colaborare se reciteste
   * dupa, fiindca de semnul asta atarna cifra lui.
   */
  const marked = useRef<string>('')
  useEffect(() => {
    if (!list.data) return
    const key = `${list.dataUpdatedAt}:${list.data.comments.filter((c) => c.unread).map((c) => c.id).join(',')}`
    if (marked.current === key) return
    marked.current = key
    void markTicketCommentsRead(id)
      .then(() => client.invalidateQueries({ queryKey: ['easeedesk', 'collaborate'] }))
      .catch(() => undefined)
  }, [client, id, list.data, list.dataUpdatedAt])

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id, 'comments'] })
  }

  /* Dupa un comentariu: lista, insignele filelor si randul de sus; dupa o
     REDESCHIDERE, toata fisa - statusul s-a schimbat, nu doar lista. */
  const afterWrite = (reply: TicketCommentReply) => {
    toast('Comment added.', 'Success', 'success')
    if (reply.ticketStatus !== undefined) {
      void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
      void client.invalidateQueries({ queryKey: ['easeedesk', 'tickets'] })
      return
    }
    void client.invalidateQueries({ queryKey: ['easeedesk', 'ticket', id] })
  }

  if (form.error) return <Failed message={form.error.message} />
  if (list.error) return <Failed message={list.error.message} />
  if (!form.data || !list.data) return <Spinner />

  const page = form.data
  const canReply = page.mode !== 'closed'

  return (
    <>
      <div className={`comments-history-${id} tcf-history`}>
        <CommentsHistory id={id} comments={list.data.comments} canReply={canReply} onWritten={afterWrite} />
      </div>

      {!hidden && (
        <div className={`new-comment-${id} tcf-panel`}>
          {page.mode !== 'open' && <Notice notice={page.notice} />}
          {page.canComment && (
            <div className="refresh-comments tcf-refresh">
              <button type="button" className={`btn btn-sm btn-primary refresh-comments-list-${id}`} onClick={refresh}>
                <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh list
              </button>
            </div>
          )}
          {page.mode !== 'open' && (
            <div className="tcf-new-ticket">
              <button type="button" className="btn btn-sm btn-success pull-right" onClick={() => setCreating(true)}>
                <i className="fas fa-plus-circle" aria-hidden="true"></i> New ticket
              </button>
            </div>
          )}
          {page.canComment && <CollaborateForm id={id} page={page} onWritten={afterWrite} />}
          {page.canComment && (
            <button type="button" className={`btn btn-xs btn-default tcf-hide show-comment-form-${id}`} onClick={() => setHidden(true)}>
              <i className="fa fa-angle-double-up" aria-hidden="true"></i> Hide
            </button>
          )}
        </div>
      )}

      {creating && (
        <Modal
          id="createTicketModal"
          open
          large
          onClose={() => setCreating(false)}
          title={
            <>
              <i className="fas fa-ticket-alt" aria-hidden="true"></i> New ticket
            </>
          }
          labelledBy="createTicketModalLabel"
        >
          <TicketCreateForm onCancel={() => setCreating(false)} onDone={() => setCreating(false)} />
        </Modal>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ */
/*  Lista: `commentsHistory` / `commentDetails` / `childDetails`             */
/* ------------------------------------------------------------------------ */

function CommentsHistory({
  id,
  comments,
  canReply,
  onWritten,
}: {
  id: number
  comments: TicketComment[]
  canReply: boolean
  onWritten(reply: TicketCommentReply): void
}) {
  const [replyTo, setReplyTo] = useState<number | null>(null)
  const now = new Date()

  if (comments.length === 0) {
    return (
      <div className="pretty-alert info">
        <i className="far fa-comments" aria-hidden="true"></i>
        <p>No comments for this ticket</p>
      </div>
    )
  }

  return (
    <>
      {comments
        .filter((c) => c.parent === null)
        .map((comment) => (
          <div className="ticket-comment" key={comment.id}>
            <div className="tcf-row">
              <div className="tcf-avatar">
                <i className="fa fa-user-circle font-22" aria-hidden="true"></i>
              </div>
              <div className="tcf-meta">
                <span className="badge mini-badge" style={{ background: comment.typeColor || '#DDDDDD' }}>
                  {comment.typeName}
                </span>{' '}
                <span className="color-green3">{comment.author}</span> <VisibilityBadge visibility={comment.visibility} /> comment:
                <span className="color-green3"> - {since(comment.created, now)}</span>
              </div>
              {canReply && (
                <div className="comment-actions tcf-actions">
                  <button
                    type="button"
                    className="btn btn-xs btn-danger"
                    aria-expanded={replyTo === comment.id}
                    onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                  >
                    <i className="fa fa-share" aria-hidden="true"></i> Reply
                  </button>
                </div>
              )}
            </div>
            {/* Textul e HTML-ul din baza (`nl2br` la salvare), desenat ca in PComments. */}
            <div className="tcf-body" dangerouslySetInnerHTML={{ __html: comment.comment }}></div>
            <div className={`collapse comment-reply-${id}${replyTo === comment.id ? ' in tcf-reply-open' : ''}`} id={`comment-reply-${comment.id}`}>
              {replyTo === comment.id && (
                <ReplyForm
                  id={id}
                  parent={comment.id}
                  onWritten={(reply) => {
                    setReplyTo(null)
                    onWritten(reply)
                  }}
                />
              )}
            </div>

            {comments
              .filter((child) => child.parent === comment.id)
              .map((child) => (
                <div className="ticket-comment tcf-child" key={child.id}>
                  <div className="tcf-row">
                    <div className="tcf-avatar">
                      <i className="fa fa-user-circle font-22" aria-hidden="true"></i>
                    </div>
                    <div className="tcf-meta">
                      <span className="color-green3">{child.author}</span> <VisibilityBadge visibility={child.visibility} /> replied:
                      <span className="color-green3"> - {since(child.created, now)}</span>
                    </div>
                  </div>
                  <div className="tcf-body" dangerouslySetInnerHTML={{ __html: child.comment }}></div>
                </div>
              ))}
            <hr className="dotted" />
          </div>
        ))}
    </>
  )
}

/* ------------------------------------------------------------------------ */
/*  Raspunsul: `replyForm` - casuta scunda, Send, Attach file                */
/* ------------------------------------------------------------------------ */

function ReplyForm({ id, parent, onWritten }: { id: number; parent: number; onWritten(reply: TicketCommentReply): void }) {
  const [text, setText] = useState('')
  const attach = useFiles()

  const send = useMutation({
    mutationFn: () => addTicketComment(id, { comment: text, parentComment: parent, files: attach.files }),
    onSuccess: (reply) => {
      setText('')
      attach.reset()
      onWritten(reply)
    },
  })

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (text.trim() === '' || send.isPending) return
    send.mutate()
  }

  return (
    <form className={`reply-comment-form-${parent} tcf-reply`} method="post" encType="multipart/form-data" onSubmit={submit}>
      <div className="form-group">
        <textarea
          className="form-control tcf-reply-text"
          required
          name="comment"
          aria-label="Reply"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
        ></textarea>
        <input type="hidden" name="ticket" value={id} />
        <input type="hidden" name="parent-comment" value={parent} />
      </div>
      <AttachedFiles files={attach.files} onRemove={attach.remove} />
      {send.isError && <p className="text-danger tcf-error">{(send.error as Error).message}</p>}
      <button type="submit" className="btn btn-default btn-primary btn-sm" disabled={text.trim() === '' || send.isPending}>
        {send.isPending ? 'Saving...' : 'Send'}
      </button>{' '}
      <button type="button" className="btn btn-default btn-sm attach-file-comment" onClick={attach.pick}>
        <i className="fa fa-paperclip" aria-hidden="true"></i> Attach file
      </button>
      <input type="file" name="files[]" className="hidden comment-attachments" ref={attach.input} onChange={attach.onChange} />
    </form>
  )
}

/* ------------------------------------------------------------------------ */
/*  Formularul filei: `buildCommentForm`                                     */
/* ------------------------------------------------------------------------ */

function CollaborateForm({ id, page, onWritten }: { id: number; page: TicketCommentFormPage; onWritten(reply: TicketCommentReply): void }) {
  const [text, setText] = useState('')
  const [template, setTemplate] = useState('')
  const [visibility, setVisibility] = useState(page.visibilities[0]?.value ?? 3)
  const [type, setType] = useState(0)
  const attach = useFiles()

  const send = useMutation({
    mutationFn: () =>
      addTicketComment(id, {
        comment: text,
        visibility,
        commentType: type > 0 ? type : undefined,
        files: attach.files,
      }),
    onSuccess: (reply) => {
      setText('')
      setTemplate('')
      setType(0)
      setVisibility(page.visibilities[0]?.value ?? 3)
      attach.reset()
      onWritten(reply)
    },
  })

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (text.trim() === '' || send.isPending) return
    send.mutate()
  }

  /* Sablonul ales INLOCUIESTE textul, ca `.collaborate_templates` din PComments. */
  const pickTemplate = (value: string) => {
    setTemplate(value)
    const hit = page.templates.find((t) => String(t.id) === value)
    if (hit) setText(hit.message)
  }

  /* Cate doua feluri pe rand, ca in `_commentTypes` (`col-md-6`, rand nou la fiecare al doilea). */
  const typeRows: ReactNode[] = []
  for (let i = 0; i < page.types.length; i += 2) {
    typeRows.push(
      <div className="row margin-5" key={i}>
        {page.types.slice(i, i + 2).map((one) => (
          <div className="col-md-6 tcf-type" key={one.value}>
            <SettingsRadio
              className="comment_types"
              name="comment_type"
              value={one.value}
              checked={type === one.value}
              onChange={() => setType(one.value)}
              label={
                <>
                  <span className="tcf-type-swatch" style={{ background: one.color }}></span>
                  {one.name}
                </>
              }
            />
          </div>
        ))}
      </div>,
    )
  }

  return (
    <form className={`new-comment-form-${id}`} method="post" action={page.action} encType="multipart/form-data" onSubmit={submit}>
      <div className="form-group">
        <label htmlFor={`exampleInputEmail1-${id}`}>{page.label}</label>
        {page.templates.length > 0 && (
          <div className="tcf-templates">
            <SelectPicker
              id={`comment-template-${id}`}
              name="source_option"
              title="Select an option..."
              size={10}
              value={template}
              onChange={pickTemplate}
              options={page.templates.map((t) => ({ value: String(t.id), label: t.title }))}
            />
          </div>
        )}
        <textarea
          className="form-control"
          required
          id={`exampleInputEmail1-${id}`}
          name="comment"
          value={text}
          onChange={(ev) => setText(ev.target.value)}
        ></textarea>
        <input type="hidden" name="ticket" value={id} />
      </div>

      <div className="form-group tcf-visibility">
        {page.visibilities.map((one) => (
          <span key={one.value} className="tcf-visibility-one">
            <SettingsRadio
              name="comment_visibility"
              id={`comment-visibility-${id}-${one.value}`}
              value={one.value}
              checked={visibility === one.value}
              onChange={() => setVisibility(one.value)}
              label={
                <>
                  {one.label}
                  {one.hint !== '' && <i className="fas fa-info-circle color-blue font-12 tcf-hint" title={one.hint} aria-label={one.hint}></i>}
                </>
              }
            />
          </span>
        ))}
      </div>

      {typeRows.length > 0 && <div className="tcf-types">{typeRows}</div>}

      <AttachedFiles files={attach.files} onRemove={attach.remove} />
      {send.isError && <p className="text-danger tcf-error">{(send.error as Error).message}</p>}
      <div className="tcf-buttons">
        <button type="submit" className="btn btn-default btn-primary btn-sm" disabled={text.trim() === '' || send.isPending}>
          {send.isPending ? 'Saving...' : page.submit}
        </button>{' '}
        <button type="button" className="btn btn-default btn-sm attach-file-comment" onClick={attach.pick}>
          <i className="fa fa-paperclip" aria-hidden="true"></i> {page.attach}
        </button>
        <input type="file" name="files[]" className="hidden comment-attachments" ref={attach.input} onChange={attach.onChange} />
      </div>
    </form>
  )
}
